"use client";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useJobFilterStore } from "../../store/useJobFilterStore";
import { useWorkExperienceStore } from "../../store/useWorkExperienceStore";
import { WorkExperienceForm } from "../../types/workExperience";
import TiptapEditor from "../atoms/TiptapEditor";
import { toast } from "react-toastify";
import { useCandidateProfileStore } from "../../store/useCandidateProfileStore";
import DatePicker from "react-datepicker";


type ExperienceField = keyof WorkExperienceForm;

type Props = {
  onClose: () => void;
  setWorkExperience: (data: WorkExperienceForm[]) => void;
  defaultData: WorkExperienceForm[];
};

export default function EditWorkExperienceForm({
  onClose,
  setWorkExperience,
  defaultData,
}: Props) {
  const [experiences, setExperiences] =
    useState<WorkExperienceForm[]>(defaultData);

  const { updateWorkExperiences, loading } = useWorkExperienceStore();

  const location = useLocation();
  const lang = location.pathname.startsWith("/id") ? "id" : "en";

  const { industries, jobTypes, fetchFilters } = useJobFilterStore();

  useEffect(() => {
    if (lang) {
      fetchFilters(lang, "industries");
      fetchFilters(lang, "jobTypes");
    }
  }, [lang]);

  const toDate = (value?: string) =>
    value ? new Date(value) : null;

  const toString = (date: Date | null) =>
    date ? date.toISOString().slice(0, 10) : "";

  const handleChange = (i: number, name: ExperienceField, value: any) => {
    setExperiences((prev) => {
      const updated = [...prev];
      updated[i] = { ...updated[i], [name]: value };

      if (name === "current" && value === true) {
        updated[i].endDate = "";
      }
      return updated;
    });
  };

  const addExperience = () => {
    setExperiences((prev) => [
      ...prev,
      {
        jobTitle: "",
        companyName: "",
        industry: "",
        employmentType: "",
        startDate: "",
        endDate: "",
        current: false,
        responsibilities: "",
        achievements: "",
      },
    ]);
  };

  const removeExperience = (index: number) => {
    setExperiences((prev) => prev.filter((_, i) => i !== index));
  };

  
  const { fetchCandidateProfile } = useCandidateProfileStore();
  const handleSave = async () => {
    console.log("FORM DATA:", experiences);

    const success = await updateWorkExperiences(experiences);

    if (success) {
      setWorkExperience(experiences);
      fetchCandidateProfile();
      toast.success("Work experience updated successfully");
      onClose();
    } else {
    toast.error("Failed to update work experience");
  }
  };
  return (
    <div className="space-y-10 pb-4">
      {experiences.map((exp, index) => (
        <div key={index} className="border rounded-xl p-6 space-y-6 relative">
          {experiences.length > 1 && (
            <button
              onClick={() => removeExperience(index)}
              className="absolute top-4 right-4 text-red-500 text-sm font-semibold"
            >
              ✕ {lang === "id" ? "Hapus" : "Remove"}
            </button>
          )}

          <h3 className="text-lg font-semibold">{lang === "id" ? "Pengalaman Kerja" : "Experience"} {index + 1}</h3>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">{lang === "id" ? "Judul Pekerjaan" : "Job Title"}</label>
              <input
                className="w-full border rounded-lg p-3"
                value={exp.jobTitle}
                onChange={(e) => handleChange(index, "jobTitle", e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{lang === "id" ? "Nama Perusahaan" : "Company Name"}</label>
              <input
                className="w-full border rounded-lg p-3"
                value={exp.companyName}
                onChange={(e) => handleChange(index, "companyName", e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{lang === "id" ? "Industri" : "Industry"}</label>
                <select
                  className="w-full border rounded-lg p-3"
                  value={exp.industry}
                  onChange={(e) => handleChange(index, "industry", Number(e.target.value))}
                >
                  <option value="">{lang === "id" ? "Pilih Industri" : "Select Industry"}</option>
                  {industries.map((item: any) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>

              </div>

              <div>
                <label className="text-sm font-medium">{lang === "id" ? "Tipe Pekerjaan" : "Employment Type"}</label>
                <select
                  className="w-full border rounded-lg p-3"
                  value={exp.employmentType}
                  onChange={(e) => handleChange(index, "employmentType", Number(e.target.value))}
                >
                  <option value="">{lang === "id" ? "Pilih Tipe Pekerjaan" : "Select Employment Type"}</option>
                  {jobTypes.map((type: any) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>

              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium">{lang === "id" ? "Tanggal Mulai" : "Start Date"}</label>
                <DatePicker
                  selected={toDate(exp.startDate)}
                  onChange={(date:any) =>
                    handleChange(index, "startDate", toString(date))
                  }
                  dateFormat="yyyy-MM-dd"
                  placeholderText="YYYY-MM-DD"
                  className="w-full border rounded-lg p-3"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium">{lang === "id" ? "Tanggal Berkahir" : "End Date"}</label>
                <DatePicker
                  selected={toDate(exp.endDate)}
                  onChange={(date:any) =>
                    handleChange(index, "endDate", toString(date))
                  }
                  dateFormat="yyyy-MM-dd"
                  placeholderText="YYYY-MM-DD"
                  className="w-full border rounded-lg p-3"
                  disabled={exp.current}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />

              </div>
            </div>

            <label className="flex gap-2 items-center">
              <input
                type="checkbox"
                checked={exp.current}
                onChange={(e) => handleChange(index, "current", e.target.checked)}
              />
              {lang === "id" ? "Saya masih bekerja disini" : "I'm currently working here"}
            </label>

            <div>
              <label className="text-sm font-medium">{lang === "id" ? "Tanggung Jawab Utama" : "Key Responsibilities"}</label>

              <TiptapEditor
                value={exp.responsibilities}
                onChange={(value: string) =>
                  handleChange(index, "responsibilities", value)
                }
              />
            </div>

            <div>
              <label className="text-sm font-medium">{lang === "id" ? "Pencapaian (Opsional)" : "Achievements (Optional)"}</label><TiptapEditor
                value={exp.achievements}
                onChange={(value: string) =>
                  handleChange(index, "achievements", value)
                }
              />
            </div>
          </div>
        </div>
      ))}

      <div className="flex justify-center">
        <button
          onClick={addExperience}
          className="flex items-center gap-2 text-primary font-semibold"
        >
          ➕ {lang === "id" ? "Tambah Pengalaman Kerja" : "Add Experience"}
        </button>
      </div>

      <div className="flex justify-center gap-4 pt-4 pb-2">
        <button
          onClick={onClose}
          className="px-6 py-3 rounded-lg border text-gray-500 font-semibold"
        >
          {lang === "id" ? "Batal" : "Cancel"}
        </button>
        <button
          disabled={loading}
          onClick={handleSave}
          className="px-6 py-3 rounded-lg bg-primary text-white font-semibold"
        >
          {lang === "id" ? "Simpan" : "Save"}
        </button>
      </div>
    </div>
  );
}
